/**
 * ENRICH.6: THE program name display function, shared by the subgraph and the
 * SPA so there is one implementation of the SHORT style (Jackson, 28 Sep 2026:
 * short names, level shown separately, exactly one name per goal).
 *
 * `displayProgramName(name, officialTitle, level)`:
 *
 *  - the official (CIP) title is returned unchanged;
 *  - any other name loses its leading degree words ("Master of Science in",
 *    "Bachelor's Degree in", "MS in", "B.S. -"), format tags ("(Online)",
 *    "(AS)", " - Online") and trailing degree tags (", BS", " BTech",
 *    " Certificate", " Program");
 *  - a name whose degree word contradicts the program level ("Master of ..."
 *    on a bachelor row), or that is nothing once stripped, shows the official
 *    title (the name itself when no official title is known);
 *  - an empty name shows the official title.
 *
 * Kept on purpose (29 Sep): a leading "Online" that is part of the subject
 * ("Online Learning & Teaching"; it comes off only before a degree word, as
 * in "Online Master of Science in X"), a trailing "Associate" that is the
 * profession ("Physician Associate", PROFESSION_ASSOCIATE), and the spacing
 * around a slash when that is the only change ("Paralegal / Legal Assistant").
 *
 * It never returns a list: the output is the official title, the input name,
 * or the input name with words taken off.
 *
 * Applied at read time only. Stored names (the hand-mapped overlay, published
 * labels) are never rewritten.
 *
 * The step functions below are also the first steps of the subgraph's
 * `cleanProgramName` (program-name-selection.ts), which adds the stricter
 * review rules for publishing a name from the older table.
 */

/** CPD education level ids: Certificate, Associate, Bachelor, Master. */
export type ProgramLevelId = 3 | 5 | 6 | 8;

/** The level a degree word or abbreviation names; 'doctor' is not a CPD level. */
export type NamedLevel = ProgramLevelId | 'doctor' | null;

export const PROGRAM_LEVEL_WORDS: Readonly<Record<string, ProgramLevelId>> = {
  certificate: 3,
  associate: 5,
  bachelor: 6,
  master: 8,
};

/** CPD level id from a legacy level word or an id. Null when unknown. */
export function levelIdFromName(level: number | string | null | undefined): ProgramLevelId | null {
  if (level === null || level === undefined) {return null;}
  if (typeof level === 'number') {
    return level === 3 || level === 5 || level === 6 || level === 8 ? level : null;
  }
  const word = level.trim().toLowerCase().replace(/'?s$/, '');
  if (/^\d+$/.test(word)) {return levelIdFromName(Number(word));}
  return PROGRAM_LEVEL_WORDS[word] ?? null;
}

/** Whitespace collapsed, ends trimmed and stray separators taken off the ends. */
export function tidyProgramName(s: string): string {
  return s
    .replace(/\s+,/g, ',')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ')
    .replace(/^[\s,;:|\-\u2013/]+|[\s,;:|\-\u2013/]+$/g, '')
    .trim();
}

/** Parenthetical contents that are format or code noise, not a track. */
export function isNoiseParen(inner: string): boolean {
  const s = inner.trim();
  if (!s) {return true;}
  if (/^(?:campus|online|hybrid|on-?campus|campus,\s*online|online,\s*campus|general\s+program|general|concentration)$/i.test(s)) {
    return true;
  }
  // Degree abbreviations and internal codes: BS, B.S., BSE, BAPS, MSITMUG_NT.
  if (/^(?:[A-Z]\.?){2,8}(?:_[A-Z]+)?$/.test(s)) {return true;}
  // Grade bands such as "(5-12)" are NOT noise: "Social Science (5-12)" is a
  // teaching program, and "Social Science" alone would misname it.
  if (/^(?:(?:undergraduate|graduate|post-?baccalaureate)\s+)?certificate$/i.test(s)) {return true;}
  return false;
}

export interface FormatNoiseOptions {
  /**
   * 'always' (the default, and the ENRICH.6 selection cleanup): a leading
   * "Online" comes off whatever follows it.
   * 'before-degree' (the display function, 29 Sep): it comes off only when a
   * degree word or abbreviation follows ("Online Master of Science in X",
   * "Online MBA"). Otherwise it is part of the subject and stays: "Online
   * Learning & Teaching", "Online Distance Learning".
   */
  leadingOnline?: 'always' | 'before-degree';
}

/**
 * Format noise: trademark signs, " - Online", a leading or trailing "Online",
 * "On-line", and "--" read as a separator.
 */
export function stripFormatNoise(raw: string, options: FormatNoiseOptions = {}): string {
  let s = raw.replace(/\s+/g, ' ').trim();
  // Trademark signs first: "Philosophy BA(TM)" is "Philosophy BA".
  s = s.replace(/[™®©]/g, '');
  s = s.replace(/\s*-\s*Online\b/gi, '');
  const leading = s.match(/^online\s+(?=\S)/i);
  if (leading && (options.leadingOnline !== 'before-degree' || startsWithDegree(s.slice(leading[0].length)))) {
    s = s.slice(leading[0].length);
  }
  s = s.replace(/\s+online$/i, '');
  s = s.replace(/\s+on-line\b/gi, '');
  // "--" is a separator: "Emergency Medical Services -- Paramedics".
  s = s.replace(/\s*-{2,}\s*/g, ' - ');
  return s;
}

/** Degree kinds that sit between "Bachelor of" and the subject. */
const DEGREE_KINDS =
  '(?:sciences?|arts|applied\\s+science|applied\\s+arts|fine\\s+arts|education|engineering|business\\s+administration|social\\s+work|professional\\s+studies|general\\s+studies|liberal\\s+(?:arts|studies)|applied\\s+technology|technology|music)';

const DEGREE_WORD = '(bachelor|master|associate|doctor)(?:\'?s)?';

function wordLevel(word: string): NamedLevel {
  const w = word.toLowerCase();
  if (w === 'doctor') {return 'doctor';}
  return PROGRAM_LEVEL_WORDS[w] ?? null;
}

const PREFIXES: {
  re: RegExp;
  level: (m: RegExpMatchArray) => NamedLevel;
  /** "Bachelor of X" with no "in": X may itself start with a degree kind. */
  bare?: boolean;
}[] = [
  // "Bachelor of Science in X", "Bachelor of Science, X", "Master of Education: X",
  // "Master of Sciences | X"
  {
    re: new RegExp(
      `^${DEGREE_WORD}\\s+of\\s+${DEGREE_KINDS}(?:\\s+major\\s+in\\s+|\\s+field\\s+of\\s+study\\s+in\\s+|\\s+in\\s+|\\s*,\\s*|\\s*:\\s*|\\s*\\|\\s*|\\s+-\\s+)(?=\\S)`,
      'i',
    ),
    level: (m) => wordLevel(m[1]),
  },
  // "Associate Degree Nursing", "Bachelor's Degree in X"
  {
    re: new RegExp(`^${DEGREE_WORD}\\s+degree\\s+(?:in\\s+)?(?=\\S)`, 'i'),
    level: (m) => wordLevel(m[1]),
  },
  // "Master of Public Health", "Bachelor of Business Administration", "Bachelor in X"
  {
    re: new RegExp(`^${DEGREE_WORD}\\s+(?:of|in)\\s+(?=\\S)`, 'i'),
    level: (m) => wordLevel(m[1]),
    bare: true,
  },
  // "Undergraduate Certificate in X", "Undergraduate Certificate - X"
  {
    re: /^(?:(?:undergraduate|graduate|post-?baccalaureate|professional)\s+)?certificate\s*(?:in|of|-|:)\s+(?=\S)/i,
    level: () => null,
  },
  // "UG Cert X"
  { re: /^(?:UG|GR|Grad)\s+Cert(?:ificate)?\.?\s*(?:in\s+|-\s*|:\s*)?(?=\S)/i, level: () => null },
];

/**
 * Degree abbreviations that may lead a name: "MS in X", "B.S. in X",
 * "MS-Biology", "AS Liberal Arts", "B.A. / B.S. Interdisciplinary Studies".
 * A closed list, because a name can also start with a subject acronym
 * ("HVAC Technology", "EMT Basic") that must stay.
 */
export const DEGREE_ABBREVIATIONS: ReadonlySet<string> = new Set([
  'AA', 'AS', 'AAS', 'AAT', 'AGS', 'BA', 'BS', 'BFA', 'BAS', 'BBA', 'BSN', 'BSW', 'BSE', 'BM',
  'MA', 'MS', 'MED', 'MBA', 'MFA', 'MPA', 'MPH', 'MSW', 'MSN', 'MAT', 'MHA', 'EDS',
]);

const ABBREVIATION_PREFIX =
  /^([A-Z](?:\.?[A-Za-z]){1,3}\.?)(?:\s*\/\s*([A-Z](?:\.?[A-Za-z]){1,3}\.?))?(?:\s+in\s+|\s*[-:]\s*|\s+)(?=\S)/;

function abbreviationLevel(abbr: string): NamedLevel {
  const first = abbr.charAt(0);
  if (first === 'B') {return 6;}
  if (first === 'M') {return 8;}
  if (first === 'A') {return 5;}
  return null;
}

function stripAbbreviationPrefix(s: string): { rest: string; named: NamedLevel } | null {
  const m = s.match(ABBREVIATION_PREFIX);
  if (!m) {return null;}
  const letters = (t: string | undefined) => (t ?? '').replace(/\./g, '').toUpperCase();
  if (!DEGREE_ABBREVIATIONS.has(letters(m[1]))) {return null;}
  if (m[2] && !DEGREE_ABBREVIATIONS.has(letters(m[2]))) {return null;}
  return { rest: s.slice(m[0].length), named: abbreviationLevel(letters(m[1])) };
}

export interface LeadingDegree {
  /** The name with its leading degree words taken off (tidied). */
  name: string;
  /** True when a leading degree word or abbreviation came off. */
  stripped: boolean;
  /** The level the degree words named, if any. */
  named: NamedLevel;
  /** The named level contradicts `level` ("Master of ..." on a bachelor row). */
  levelMismatch: boolean;
  /** "Bachelor of Arts Art History": where the kind ends and the subject starts is a guess. */
  ambiguousKind: boolean;
}

/**
 * SHORT style: the leading degree words come off. "Bachelor of Arts in X",
 * "Master of Education: X", "Associate Degree X", "Undergraduate Certificate -
 * X", "MS in X", "B.S. X", "B.A. / B.S. X".
 */
export function stripLeadingDegree(name: string, level?: number | string | null): LeadingDegree {
  const wanted = levelIdFromName(level ?? null);
  let s = name;
  let named: NamedLevel = null;
  let stripped = false;
  let ambiguousKind = false;
  for (const prefix of PREFIXES) {
    const m = s.match(prefix.re);
    if (m) {
      named = prefix.level(m);
      s = tidyProgramName(s.slice(m[0].length));
      stripped = true;
      if (prefix.bare && new RegExp(`^${DEGREE_KINDS}\\s+\\S`, 'i').test(s)) {ambiguousKind = true;}
      break;
    }
  }
  if (!stripped) {
    const abbreviation = stripAbbreviationPrefix(s);
    if (abbreviation && tidyProgramName(abbreviation.rest)) {
      named = abbreviation.named;
      s = tidyProgramName(abbreviation.rest);
      stripped = true;
    }
  }
  const levelMismatch = wanted !== null && named !== null && named !== wanted;
  return { name: s, stripped, named, levelMismatch, ambiguousKind };
}

/**
 * Whether a name starts with a degree word or a degree abbreviation: "Master
 * of Science in X", "Bachelor's", "MBA", "B.S. in X", "Graduate Certificate".
 */
export function startsWithDegree(name: string): boolean {
  const s = tidyProgramName(name);
  if (/^(?:bachelor|master|associate|doctor)(?:'?s)?\b/i.test(s)) {return true;}
  if (/^(?:(?:undergraduate|graduate|post-?baccalaureate)\s+)?certificate\b/i.test(s)) {return true;}
  if (stripLeadingDegree(s).stripped) {return true;}
  const first = s.split(/[\s,:/-]+/)[0] ?? '';
  return DEGREE_ABBREVIATIONS.has(first.replace(/\./g, '').toUpperCase());
}

/**
 * Names where a trailing "Associate" is the profession, not a degree tag
 * (29 Sep): "Physician Associate" (a physician assistant program under its
 * other name) and "Research Associate" / "Clinical Research Associate". A
 * small explicit list: in every other name a trailing "Associate" is the
 * degree ("Paralegal Associate", "Medical Assisting Associate") and comes off.
 */
export const PROFESSION_ASSOCIATE = /\b(?:physician|research)\s+associate$/i;

/**
 * Trailing certificate words and degree tags: ", Graduate Certificate",
 * ", BS", ", B.S.", ", MUD", " BTech", " Certificate", " Program", " Degree".
 *
 * `general` also takes a trailing " General" off ("Biology General Program").
 * The display function leaves it on, so an official title that reaches it
 * without its official twin ("Biology/Biological Sciences, General") keeps
 * its wording.
 */
export function stripTrailingDegreeWords(name: string, options: { general?: boolean } = {}): string {
  let s = tidyProgramName(name.replace(/,\s*(?:(?:undergraduate|graduate|post-?baccalaureate)\s+)?certificate$/i, ''));
  s = tidyProgramName(s.replace(/,\s*(?:[A-Z]\.?){1,5}$/, ''));
  s = tidyProgramName(s.replace(/\s+(?:BTech|BS|BA|BSN|MS|MA|MBA|MEd|AAS|AS|AA)\.?$/, ''));
  const trailing = options.general
    ? /\s+(?:certificate|program|degree|diploma|major|master|bachelor|associate|general)$/i
    : /\s+(?:certificate|program|degree|diploma|major|master|bachelor|associate)$/i;
  // Twice, so "Pastry Arts Associate Degree" loses both words.
  for (let pass = 0; pass < 2; pass++) {
    // "Physician Associate" names the profession: it keeps its last word.
    if (PROFESSION_ASSOCIATE.test(s)) {break;}
    const without = tidyProgramName(s.replace(trailing, ''));
    if (letterCount(without) >= 3) {s = without;}
  }
  return s;
}

function letterCount(s: string): number {
  return (s.match(/[A-Za-z]/g) ?? []).length;
}

/** Case, punctuation and "&" folded, so two spellings compare equal. */
export function foldProgramName(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '');
}

export interface ShortProgramName {
  /** The short name; empty when nothing usable is left. */
  name: string;
  /** Degree words came off the front. */
  strippedDegree: boolean;
  /** The name's own degree contradicts the level, or its kind/subject split is a guess. */
  unusable: boolean;
}

/**
 * The short style of one name. Tracks, concentrations and non-noise
 * parentheticals ("(5-12)", "(Biomedical Sciences)") STAY: taking them off
 * would drop part of the program's meaning.
 */
export function shortProgramName(
  name: string | null | undefined,
  level?: number | string | null,
): ShortProgramName {
  let s = stripFormatNoise(name ?? '', { leadingOnline: 'before-degree' });
  s = tidyProgramName(s.replace(/\s*\(([^()]*)\)/g, (whole, inner: string) => (isNoiseParen(inner) ? ' ' : whole)));
  const lead = stripLeadingDegree(s, level);
  s = stripTrailingDegreeWords(lead.name);
  const unusable = lead.levelMismatch || lead.ambiguousKind || letterCount(s) < 3;
  return { name: unusable ? '' : s, strippedDegree: lead.stripped, unusable };
}

/**
 * THE display function: exactly one program name for a program level, short
 * style. See the file header.
 *
 * @param name the name the server resolved (published real name, hand-mapped
 *   name, catalog name or official title)
 * @param officialTitle the official CIP title, when known
 * @param level the CPD level id (3, 5, 6, 8) or level word
 */
export function displayProgramName(
  name: string | null | undefined,
  officialTitle: string | null | undefined,
  level?: number | string | null,
): string {
  const raw = (name ?? '').replace(/\s+/g, ' ').trim();
  const official = (officialTitle ?? '').replace(/\s+/g, ' ').trim();
  if (!raw) {return official;}
  if (official && foldProgramName(raw) === foldProgramName(official)) {return official;}
  const short = shortProgramName(raw, level);
  if (short.unusable || !short.name) {return official || raw;}
  // Spacing around a slash is the only difference: show the name as written
  // ("Paralegal / Legal Assistant" stays, 29 Sep).
  if (short.name !== raw && short.name === raw.replace(/\s*\/\s*/g, '/')) {return raw;}
  return short.name;
}

/**
 * Whether a name still leads with degree words ("Master of Science in X").
 * After `displayProgramName` that is only a name kept whole because its degree
 * contradicts the level and no official title was known; a surface that puts
 * the level word in front must not do so for it.
 */
export function leadsWithDegree(name: string | null | undefined): boolean {
  return stripLeadingDegree(tidyProgramName(name ?? '')).stripped;
}

/** The level word a one-line program goal starts with (Jackson, 28 Sep). */
export const PROGRAM_GOAL_LEVEL_WORD: Readonly<Record<ProgramLevelId, string>> = {
  3: 'Certificate',
  5: "Associate's",
  6: "Bachelor's",
  8: "Master's",
};

/**
 * A program goal as ONE line: the level word, then the one short name.
 * "Bachelor's in Human Development and Family Studies". Used by the Goals
 * Report, its PDF, the hero goal card, the goal-set dialog and the brag sheet.
 *
 * The level comes from the goal, so a name carrying its own degree loses it
 * and the degree is never said twice. A name kept whole because its degree
 * contradicts the level (no official title to fall back on) is shown as it
 * is, without a level word that would disagree with it. With no known level
 * the name is shown as it is: its own degree words are then the only level
 * the line has.
 */
export function programGoalLine(
  name: string | null | undefined,
  officialTitle: string | null | undefined,
  level: number | string | null | undefined,
): string {
  const id = levelIdFromName(level ?? null);
  if (id === null) {
    return (name ?? '').replace(/\s+/g, ' ').trim() || (officialTitle ?? '').trim();
  }
  const shown = displayProgramName(name, officialTitle, id);
  if (!shown || leadsWithDegree(shown)) {
    return shown;
  }
  return `${PROGRAM_GOAL_LEVEL_WORD[id]} in ${shown}`;
}
